import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type {
  Cadence,
  CreateTodoListInput,
  TaskInput,
  TodoList,
  UpdateTodoListInput,
} from '@/types/admin/todoLists';

/**
 * RHF wiring + client validation + submit mapping for the to-do list create/edit
 * form. The validation here MIRRORS the server's Zod rules
 * (controllers/todo-lists/schemas/todo-lists.schemas.ts) so a well-formed form
 * never trips a 422:
 *   - title: trimmed, non-empty
 *   - cadence: one of daily|weekly|monthly
 *   - target: EXACTLY one of roleId / assigneeAccountId (XOR)
 *   - window: endsOn >= startsOn when both present (create may omit startsOn —
 *     the server defaults it to today in the property tz)
 *   - tasks: an empty list is a WARN, not an error (submit still permitted)
 *
 * Task edit mapping follows updateTodoList's task-sync semantics
 * (services/todo-lists/todo-lists.service.ts): a kept existing row is re-sent
 * with its `{ id, title, sortOrder }` (which also reactivates a soft-removed
 * task server-side); a removed existing row is sent as `{ id, remove: true }`
 * (soft-remove); a new row is sent as `{ title, sortOrder }` with no id; a new
 * row that the user removes before saving is simply dropped.
 */

export const CADENCE_OPTIONS: { value: Cadence; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/** Explainer copy for the apply-now control (edit only). */
export const APPLY_NOW_COPY = 'Also update the current period’s list';

/** A single row in the ordered tasks editor. `id` is set for existing tasks. */
export interface TodoListItemRow {
  /** Stable client key for React lists (never sent to the server). */
  key: string;
  /** Server id for an existing definition task; undefined for a new row. */
  id?: number;
  title: string;
  /** True when an existing task is marked for soft-removal. */
  removed: boolean;
}

interface FormValues {
  title: string;
  cadence: Cadence;
  roleId: number | null;
  assigneeAccountId: number | null;
  startsOn: string;
  endsOn: string;
}

export interface TodoListFormErrors {
  title?: string;
  target?: string;
  window?: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `row-${keySeq}`;
}

function rowsFromInitial(initial?: TodoList): TodoListItemRow[] {
  if (!initial?.tasks) return [];
  return initial.tasks
    .filter(task => task.isActive)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(task => ({
      key: nextKey(),
      id: task.id,
      title: task.title,
      removed: false,
    }));
}

interface UseTodoListFormArgs {
  mode: 'create' | 'edit';
  propertyId: number;
  initial?: TodoList;
}

export function useTodoListForm({
  mode,
  propertyId,
  initial,
}: UseTodoListFormArgs) {
  const { control, watch, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      title: initial?.title ?? '',
      cadence: initial?.cadence ?? 'daily',
      roleId: initial?.roleId ?? null,
      assigneeAccountId: initial?.assigneeAccountId ?? null,
      startsOn: initial?.startsOn ?? '',
      endsOn: initial?.endsOn ?? '',
    },
  });

  const [rows, setRows] = useState<TodoListItemRow[]>(() =>
    rowsFromInitial(initial),
  );
  const [errors, setErrors] = useState<TodoListFormErrors>({});

  const title = watch('title');
  const cadence = watch('cadence');
  const roleId = watch('roleId');
  const assigneeAccountId = watch('assigneeAccountId');
  const startsOn = watch('startsOn');
  const endsOn = watch('endsOn');

  const setField = useCallback(
    <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
      // `field`/`value` are known-simple keys of FormValues; RHF's Path-based
      // generic can't infer that, so widen to its expected argument types.
      setValue(field as Parameters<typeof setValue>[0], value as never, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  // XOR target selection: picking one clears + (via the disabled prop in the
  // picker) locks out the other, exactly as the server's update patch does.
  const selectRole = useCallback(
    (id: number) => {
      setValue('roleId', id, { shouldDirty: true });
      setValue('assigneeAccountId', null, { shouldDirty: true });
      setErrors(prev => ({ ...prev, target: undefined }));
    },
    [setValue],
  );

  const selectPerson = useCallback(
    (id: number) => {
      setValue('assigneeAccountId', id, { shouldDirty: true });
      setValue('roleId', null, { shouldDirty: true });
      setErrors(prev => ({ ...prev, target: undefined }));
    },
    [setValue],
  );

  // ----- tasks editor -----
  const addTask = useCallback(() => {
    setRows(prev => [...prev, { key: nextKey(), title: '', removed: false }]);
  }, []);

  const renameTask = useCallback((index: number, value: string) => {
    setRows(prev =>
      prev.map((row, i) => (i === index ? { ...row, title: value } : row)),
    );
  }, []);

  const removeTask = useCallback((index: number) => {
    setRows(prev => {
      const row = prev[index];
      if (!row) return prev;
      // An existing task is soft-removed (kept in the list, greyed out); a brand
      // new row is dropped outright.
      if (row.id != null) {
        return prev.map((r, i) => (i === index ? { ...r, removed: true } : r));
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Clear a soft-remove so the greyed row rejoins the list (and the payload).
  // Only existing rows are ever in a `removed` state — new rows vanish outright.
  const undoRemoveTask = useCallback((index: number) => {
    setRows(prev =>
      prev.map((r, i) => (i === index ? { ...r, removed: false } : r)),
    );
  }, []);

  const moveTask = useCallback((index: number, direction: -1 | 1) => {
    setRows(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  }, []);

  // ----- validation (mirrors the Zod contract) -----
  const validate = useCallback((): boolean => {
    const values = getValues();
    const next: TodoListFormErrors = {};

    if (!values.title.trim()) {
      next.title = 'A title is required';
    }

    const hasRole = values.roleId != null;
    const hasAssignee = values.assigneeAccountId != null;
    if (hasRole === hasAssignee) {
      next.target = 'Choose exactly one target — a role or a person';
    }

    const s = values.startsOn.trim();
    const e = values.endsOn.trim();
    if (s && !DATE_RE.test(s)) {
      next.window = 'Start date must be YYYY-MM-DD';
    } else if (e && !DATE_RE.test(e)) {
      next.window = 'End date must be YYYY-MM-DD';
    } else if (s && e && e < s) {
      // Lexicographic comparison is valid for YYYY-MM-DD (matches the backend).
      next.window = 'End date must be on or after the start date';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }, [getValues]);

  /** True when the (active, non-removed) to-do list is empty — a warn, not a block. */
  const isTasksEmpty = useMemo(
    () => rows.filter(row => !row.removed && row.title.trim()).length === 0,
    [rows],
  );

  /** Build the ordered TaskInput[] for the request per create/update semantics. */
  const buildTasks = useCallback((): TaskInput[] => {
    const result: TaskInput[] = [];
    let sortOrder = 0;
    for (const row of rows) {
      if (row.removed) {
        // Only existing (id-bearing) rows can be soft-removed; a removed new row
        // was already filtered out of `rows` by removeTask.
        if (row.id != null) result.push({ id: row.id, remove: true });
        continue;
      }
      const trimmed = row.title.trim();
      if (!trimmed) continue; // skip blank rows entirely
      if (row.id != null) {
        result.push({ id: row.id, title: trimmed, sortOrder });
      } else {
        result.push({ title: trimmed, sortOrder });
      }
      sortOrder += 1;
    }
    return result;
  }, [rows]);

  /** Assemble the create payload once validation has passed. */
  const buildCreatePayload = useCallback((): CreateTodoListInput => {
    const values = getValues();
    const tasks = buildTasks();
    const payload: CreateTodoListInput = {
      propertyId,
      title: values.title.trim(),
      cadence: values.cadence,
      roleId: values.roleId,
      assigneeAccountId: values.assigneeAccountId,
      tasks,
    };
    if (values.startsOn.trim()) payload.startsOn = values.startsOn.trim();
    if (values.endsOn.trim()) payload.endsOn = values.endsOn.trim();
    return payload;
  }, [buildTasks, getValues, propertyId]);

  /**
   * Assemble the update payload once validation has passed. `applyNow` (from the
   * edit-only toggle) rides in the SAME PATCH body so the server can re-sync the
   * current occurrence atomically — no separate apply-now request, no race.
   */
  const buildUpdatePayload = useCallback(
    (applyNow: boolean): UpdateTodoListInput => {
      const values = getValues();
      const payload: UpdateTodoListInput = {
        title: values.title.trim(),
        cadence: values.cadence,
        roleId: values.roleId,
        assigneeAccountId: values.assigneeAccountId,
        endsOn: values.endsOn.trim() ? values.endsOn.trim() : null,
        tasks: buildTasks(),
        applyNow,
      };
      if (values.startsOn.trim()) payload.startsOn = values.startsOn.trim();
      return payload;
    },
    [buildTasks, getValues],
  );

  return {
    control,
    // watched values
    title,
    cadence,
    roleId,
    assigneeAccountId,
    startsOn,
    endsOn,
    // errors + task state
    errors,
    rows,
    isTasksEmpty,
    // setters
    setField,
    selectRole,
    selectPerson,
    addTask,
    renameTask,
    removeTask,
    undoRemoveTask,
    moveTask,
    // submit helpers
    validate,
    buildCreatePayload,
    buildUpdatePayload,
    mode,
  };
}
